'use client'

import { useState } from 'react'
import { cn } from '@/lib/tailwind'
import { FOCUS_STYLES } from '@/ui/design-tokens'
import { ResponsiveSelector } from '@/ui/responsive-selector'
import { DateField } from './date-field'

interface InlinePropertyPopoverProps<T extends string | number> {
  /** Current selected value */
  value: T | null
  /** Display label for the current value (if different from value) */
  valueLabel?: string
  /** Placeholder text when no value is selected */
  placeholder?: string
  /** Options to display in the popover */
  options: Array<{
    value: T
    label: string
    icon?: React.ComponentType<{
      className?: string
      weight?: 'duotone' | 'fill'
    }>
  }>
  /** Called when an option is selected */
  onChange: (value: T | null) => void
  /** Optional icon to display before the value */
  icon?: React.ReactNode
  /** Whether to show clear option */
  allowClear?: boolean
  /** Clear button label */
  clearLabel?: string
  /** Whether the popover is disabled */
  disabled?: boolean
  /** Custom className for the trigger button */
  className?: string
  /** Color function for value text */
  getValueColor?: (value: T | null) => string
  /** Popover alignment */
  align?: 'start' | 'center' | 'end'
}

/**
 * Inline property popover for selecting values in a natural sentence format.
 * Used in unified form for theme, mood, category, priority, etc.
 * Uses ResponsiveSelector: popover on desktop, bottom sheet on mobile.
 */
export function InlinePropertyPopover<T extends string | number>({
  value,
  valueLabel,
  placeholder = 'select',
  options,
  onChange,
  icon,
  allowClear = false,
  clearLabel = 'Clear',
  disabled = false,
  className,
  getValueColor,
  align = 'start',
}: InlinePropertyPopoverProps<T>) {
  const [open, setOpen] = useState(false)
  const displayLabel =
    valueLabel ?? options.find((o) => o.value === value)?.label ?? placeholder
  const hasValue = value !== null && value !== undefined

  const handleSelect = (optionValue: T) => {
    onChange(optionValue)
    setOpen(false)
  }

  const handleClear = () => {
    onChange(null)
    setOpen(false)
  }

  return (
    <ResponsiveSelector
      open={open}
      onOpenChange={setOpen}
      ariaLabel={placeholder}
      align={align}
      trigger={
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-sm transition-colors',
            'hover:bg-muted/30',
            hasValue
              ? 'text-foreground'
              : 'text-muted-foreground hover:text-foreground',
            FOCUS_STYLES.ringCompact,
            disabled && 'cursor-not-allowed opacity-50',
            className,
          )}
        >
          {icon}
          <span>{displayLabel}</span>
        </button>
      }
    >
      <div className="space-y-0.5">
        {options.map((option) => {
          const OptionIcon = option.icon
          const isSelected = value === option.value
          const optionColor = getValueColor?.(option.value)

          return (
            <button
              key={String(option.value)}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={cn(
                'flex min-h-[44px] w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors sm:min-h-[32px]',
                isSelected
                  ? 'bg-accent text-accent-foreground'
                  : 'hover:bg-muted',
                optionColor,
              )}
            >
              {OptionIcon && <OptionIcon className="h-3.5 w-3.5" />}
              {option.label}
            </button>
          )
        })}

        {allowClear && hasValue && (
          <>
            <div className="bg-border my-1 h-px" />
            <button
              type="button"
              onClick={handleClear}
              className="text-muted-foreground hover:bg-muted flex min-h-[44px] w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors sm:min-h-[32px]"
            >
              {clearLabel}
            </button>
          </>
        )}
      </div>
    </ResponsiveSelector>
  )
}

interface DatePropertyPopoverProps {
  /** Current selected date */
  value: Date | undefined
  /** Called when date changes */
  onChange: (date: Date | null) => void
  /** Whether the popover is disabled */
  disabled?: boolean
  /** Calendar component to render (ignored -- kept for backward compat, uses DateField internally) */
  calendar?: React.ReactNode
  /** Display format for the date */
  formatDate?: (date: Date) => string
  /** Placeholder when no date */
  placeholder?: string
}

/**
 * Inline date popover for selecting dates in a sentence format.
 * Delegates to DateField which uses ResponsiveSelector internally.
 */
export function DatePropertyPopover({
  value,
  onChange,
  disabled = false,
  formatDate = (d) => d.toLocaleDateString(),
  placeholder = '+ due',
}: DatePropertyPopoverProps) {
  return (
    <DateField
      value={value}
      onChange={onChange}
      disabled={disabled}
      formatDate={formatDate}
      placeholder={placeholder}
      clearable={!!value}
      variant="inline"
    />
  )
}
